<template>
  <div>
    <v-row class="ma-0 my-4 align-center justify-space-between">
      <h1 class="text-h5 font-weight-bold">Administradores</h1>
      <v-btn color="primary" rounded @click="openCreateDialog">
        <v-icon left small>mdi-plus</v-icon>Crear administrador
      </v-btn>
    </v-row>

    <v-alert v-if="error" type="error" dense outlined class="mb-3">{{ error }}</v-alert>

    <v-row>
      <v-col cols="12">
        <v-card outlined>
          <v-row class="px-4 pt-4 pb-2">
            <v-col cols="12" sm="5" class="py-0">
              <v-select
                v-model="filterStatus"
                :items="filterStatusOptions"
                item-text="label"
                item-value="value"
                dense
                hide-details
                outlined
                label="Estado"
                @change="loadEjecutivos"
              ></v-select>
            </v-col>
          </v-row>
          <v-data-table
            :headers="headers"
            :items="ejecutivos"
            :loading="loading"
            hide-default-footer
            :items-per-page="-1"
            class="elevation-0"
            no-data-text="No hay ejecutivos registrados"
          >
            <template v-slot:item.name="{ item }">
              {{ item.name }} {{ item.surname }}
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip x-small :color="item.status === 1 ? 'success' : 'error'" dark>
                {{ item.status === 1 ? 'Activo' : 'Inactivo' }}
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    x-small
                    depressed
                    rounded
                    v-bind="attrs"
                    v-on="on"
                    :color="item.status === 1 ? 'error' : 'success'"
                    :loading="togglingId === item.id"
                    :disabled="!!togglingId"
                    @click="toggleStatus(item)"
                  >
                    {{ item.status === 1 ? 'Desactivar' : 'Activar' }}
                  </v-btn>
                </template>
                <span>{{ item.status === 1 ? 'Desactivar usuario' : 'Activar usuario' }}</span>
              </v-tooltip>
              <v-tooltip top>
                <!-- The activator is the span, not the button: a disabled v-btn
                     emits no mouse events, so the tooltip explaining WHY it is
                     disabled would never appear. -->
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on" class="d-inline-block ml-2">
                    <v-btn
                      x-small
                      depressed
                      rounded
                      :color="item.mfaEnabled ? 'warning' : 'grey lighten-2'"
                      :loading="resettingId === item.id"
                      :disabled="!!resettingId || !item.mfaEnabled"
                      @click="askResetMfa(item)"
                    >
                      <v-icon x-small left>
                        {{ item.mfaEnabled ? 'mdi-shield-refresh' : 'mdi-shield-alert' }}
                      </v-icon>
                      {{ item.mfaEnabled ? 'Resetear MFA' : 'MFA pendiente' }}
                    </v-btn>
                  </span>
                </template>
                <span>
                  {{ item.mfaEnabled
                    ? 'Eliminar su segundo factor para que registre un dispositivo nuevo'
                    : 'Aún no vincula una app de autenticación. No puede operar el panel.' }}
                </span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="createDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="text-h6">Crear administrador</v-card-title>
        <v-card-text>
          <v-form ref="createForm" v-model="createValid" lazy-validation @submit.prevent="submitCreate">
            <v-text-field v-model="newAdmin.name" :rules="requiredRule" label="Nombre" required></v-text-field>
            <v-text-field v-model="newAdmin.surname" :rules="requiredRule" label="Apellido" required></v-text-field>
            <v-text-field v-model="newAdmin.email" :rules="emailRules" label="Correo electrónico" required></v-text-field>
          </v-form>
          <v-alert v-if="createError" dense type="error" class="mb-0">{{ createError }}</v-alert>
          <p class="text-caption mb-0 mt-3">
            Recibirá su contraseña por correo. En su primer ingreso deberá registrar
            la verificación en dos pasos antes de poder operar el panel.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="creating" @click="createDialog = false">Cancelar</v-btn>
          <v-btn color="primary" text :loading="creating" @click="submitCreate">Crear</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" color="success" timeout="5000">{{ snackbarText }}</v-snackbar>

    <v-dialog v-model="confirmMfaReset" max-width="420">
      <v-card>
        <v-card-title class="text-h6">Resetear verificación en dos pasos</v-card-title>
        <v-card-text>
          Se eliminará el segundo factor de <strong>{{ mfaResetTarget && mfaResetTarget.name }}</strong>
          y sus códigos de recuperación dejarán de servir. Deberá registrar un dispositivo nuevo en su
          próximo ingreso.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmMfaReset = false">Cancelar</v-btn>
          <v-btn color="warning" text @click="resetMfa">Resetear</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import EjecutivosService from '../services/ejecutivos.service';
import MfaService from '../services/mfa.service';

export default {
  name: 'Ejecutivos',
  data() {
    return {
      loading: false,
      error: null,
      togglingId: null,
      resettingId: null,
      confirmMfaReset: false,
      mfaResetTarget: null,
      createDialog: false,
      createValid: true,
      creating: false,
      createError: null,
      newAdmin: { name: '', surname: '', email: '' },
      requiredRule: [v => !!(v && v.trim()) || 'Campo requerido'],
      emailRules: [
        v => !!v || 'El correo electrónico es requerido',
        v => /.+@.+\..+/.test(v) || 'El correo debe ser válido'
      ],
      snackbar: false,
      snackbarText: '',
      ejecutivos: [],
      filterStatus: 'active',
      filterStatusOptions: [
        { label: 'Solo activos', value: 'active' },
        { label: 'Todos',        value: 'all' },
      ],
      headers: [
        { text: 'Nombre',   value: 'name',    sortable: true },
        { text: 'Email',    value: 'email',   sortable: true },
        { text: 'Estado',   value: 'status',  sortable: false },
        { text: 'Acciones', value: 'actions', sortable: false },
      ],
    };
  },
  mounted() {
    this.loadEjecutivos();
  },
  methods: {
    async loadEjecutivos() {
      this.loading = true;
      const all = this.filterStatus === 'all';
      const res = await EjecutivosService.getAll(all);
      if (res.status === 'ok') this.ejecutivos = res.data;
      else this.error = 'No se pudieron cargar los ejecutivos.';
      this.loading = false;
    },
    async toggleStatus(item) {
      this.togglingId = item.id;
      const res = await EjecutivosService.toggleStatus(item.id);
      if (res.status === 'ok') {
        item.status = item.status === 1 ? 0 : 1;
      } else {
        this.error = 'No se pudo cambiar el estado del usuario.';
      }
      this.togglingId = null;
    },
    openCreateDialog() {
      this.newAdmin = { name: '', surname: '', email: '' };
      this.createError = null;
      this.createDialog = true;
      if (this.$refs.createForm) this.$refs.createForm.resetValidation();
    },
    async submitCreate() {
      if (!this.$refs.createForm.validate()) return;

      this.creating = true;
      this.createError = null;
      const res = await EjecutivosService.create(this.newAdmin);
      if (res.status === 'ok') {
        this.createDialog = false;
        this.snackbarText = `Administrador creado. Se envió la contraseña a ${this.newAdmin.email}.`;
        this.snackbar = true;
        await this.loadEjecutivos();
      } else if (res.code === 'USER_ADMIN_ALREADY_EXISTS') {
        this.createError = 'Ya existe un administrador con ese correo.';
      } else {
        this.createError = res.message || 'No se pudo crear el administrador.';
      }
      this.creating = false;
    },
    askResetMfa(item) {
      this.mfaResetTarget = item;
      this.confirmMfaReset = true;
    },
    async resetMfa() {
      const target = this.mfaResetTarget;
      this.confirmMfaReset = false;
      if (!target) return;

      this.resettingId = target.id;
      const res = await MfaService.reset(target.id);
      if (res.status === 'ok') {
        this.snackbarText = `Se reseteó el segundo factor de ${target.name}.`;
        this.snackbar = true;
        // Reload so the MFA chip flips to "Pendiente" and the button disables.
        await this.loadEjecutivos();
      } else if (res.code === 'OPERATION_NOT_AUTHORIZED') {
        this.error = 'No puedes resetear tu propio segundo factor.';
      } else {
        this.error = 'No se pudo resetear el segundo factor del usuario.';
      }
      this.resettingId = null;
      this.mfaResetTarget = null;
    },
  },
};
</script>
